import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ScanLine, Sparkles, CreditCard, ArrowRight } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="neivai-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <Icon className="h-4 w-4 text-[#C86B53]" strokeWidth={1.5} />
      </div>
      <p className="mt-6 text-4xl font-display font-semibold tracking-tight">{value}</p>
    </div>
  );
}

export default function Overview() {
  const { user, refresh } = useAuth();
  const [products, setProducts] = useState([]);
  const [renders, setRenders] = useState([]);

  useEffect(() => {
    refresh();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/history").then((r) => setRenders(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  return (
    <div className="space-y-10" data-testid="dashboard-overview">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="overline">Overview</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">Hi, {user?.business_name}.</h1>
          <p className="mt-2 text-[#5C5F62]">Your visualization studio at a glance.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/dashboard/products" className="neivai-btn-secondary inline-flex items-center gap-2">
            Add products <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/dashboard/qr" data-testid="get-qr-btn" className="neivai-btn-primary inline-flex items-center gap-2">
            Get store QR <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Stat icon={Package} label="Products" value={products.length} testid="stat-products" />
        <Stat icon={ScanLine} label="Total renders" value={renders.length} testid="stat-renders" />
        <Stat icon={Sparkles} label="This week" value={renders.filter((r) => Date.now() - new Date(r.created_at).getTime() < 7*24*3600*1000).length} testid="stat-week" />
        <Stat icon={CreditCard} label="Credits left" value={user?.credits ?? 0} testid="stat-credits" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 neivai-card p-8">
          <h3 className="text-2xl font-display font-medium tracking-tight">Recent renders</h3>
          {renders.length === 0 ? (
            <div className="mt-6 border border-dashed border-[#E5E5E5] rounded-lg p-10 text-center">
              <p className="text-[#8A8D91]">No customer renders yet. Print your QR &amp; place it in-store.</p>
              <Link to="/dashboard/qr" className="mt-4 inline-flex text-[#C86B53] hover:underline">Get your QR code →</Link>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {renders.slice(0, 8).map((r) => (
                <a
                  key={r.id}
                  href={resolveMediaUrl(r.output_urls?.[0])}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square rounded-lg overflow-hidden bg-[#F3F1EC] block"
                >
                  {r.output_urls?.[0] ? (
                    <img src={resolveMediaUrl(r.output_urls[0])} alt="render" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xs text-[#8A8D91]">pending</div>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="neivai-card p-8">
          <h3 className="text-2xl font-display font-medium tracking-tight">Quick tips</h3>
          <ul className="mt-6 space-y-4 text-sm text-[#5C5F62]">
            <li>1. Upload your top-selling products first.</li>
            <li>2. Print QR at eye level near your best display.</li>
            <li>3. Ask customers to share the render on WhatsApp.</li>
            <li>4. Buy more credits before peak weekend traffic.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
